import { useToast } from "@/hooks/useToast";
import { IS_MOBILE } from "@/lib/environmentDetector";
import { createDMChat } from "@/middleware/chats";
import { useNavigationState } from "@/zustand/NavigationState";
import ChatMemberDesktopMenu from "./ChatMemberDesktopMenu";
import { updateMemberRole } from "@/middleware/members";
import { MemberRole } from "@/server/types/member";
import ChatMemberMobileMenu from "./ChatMemberMobileMenu";
import { queryClient } from "@/queryClient";
import { useTranslation } from "react-i18next";

type ChatMemberMenuProps = {
  username: string;
  role: MemberRole;
};

export const getAssignableRoles = (
  currentUserRole: MemberRole,
  targetUserRole: MemberRole,
): MemberRole[] => {
  // Only allow if target is lower than curr user
  if (targetUserRole <= currentUserRole) {
    return [];
  }

  const assignable: MemberRole[] = [];

  for (const role of Object.values(MemberRole)) {
    if (
      typeof role === "number" &&
      role > currentUserRole // Only roles lower in hierarchy
    ) {
      assignable.push(role);
    }
  }

  return assignable;
};

export default function ChatMemberMenu({
  username,
  role,
}: ChatMemberMenuProps) {
  const activeChatId = useNavigationState((state) => state.activeChatId);
  const { t } = useTranslation();
  const { toast } = useToast();

  const handleCreateDMChat = async () => {
    if (!activeChatId) {
      return;
    }
    try {
      await createDMChat(username);
    } catch (error) {
      toast({
        duration: 1000,
        description:
          error instanceof Error ? error.message : t("errorOccurred"),
        variant: "destructive",
      });
    }
  };

  const handleChangeMemberRole = async (newRole: MemberRole) => {
    if (!activeChatId) {
      return;
    }

    try {
      await updateMemberRole(activeChatId, username, newRole);

      toast({
        duration: 1500,
        description: `Member role updated successfully!`,
        variant: "default",
      });

      queryClient.invalidateQueries({
        queryKey: ["chat-members", activeChatId],
      });
    } catch (error) {
      toast({
        duration: 1000,
        description:
          error instanceof Error ? error.message : t("errorOccurred"),
        variant: "destructive",
      });
    }
  };

  if (IS_MOBILE) {
    return (
      <ChatMemberMobileMenu
        username={username}
        memberRole={role}
        onStartP2PChat={handleCreateDMChat}
        onChangeMemberRole={handleChangeMemberRole}
      />
    );
  }
  return (
    <ChatMemberDesktopMenu
      username={username}
      memberRole={role}
      onStartP2PChat={handleCreateDMChat}
      onChangeMemberRole={handleChangeMemberRole}
    />
  );
}
